/**
 * i18n resources for the `integrations` feature slice (PMS configuration +
 * capabilities + test/status/disable).
 *
 * Shape: `{ namespace, resources: { en, bg, de, fr } }`. Non-English locales
 * start as English placeholders so key-parity passes (master prompt §13).
 */

export const namespace = 'integrations' as const;

const en = {
  page: {
    eyebrow: 'Settings',
    title: 'Integrations',
    description:
      'Connect your property management system so the assistant can read and write reservations.',
  },
  config: {
    title: 'Configure PMS',
    description: 'Credentials are write-only — they are sent securely and never displayed again.',
    providerLabel: 'Provider',
    providerPlaceholder: 'e.g. opera, mews',
    apiKeyLabel: 'API key',
    apiKeyPlaceholder: 'Enter API key',
    apiSecretLabel: 'API secret',
    apiSecretPlaceholder: 'Enter API secret',
    baseUrlLabel: 'Base URL',
    baseUrlPlaceholder: 'https://pms.example.com',
    writeOnlyNote: 'For security, saved credentials are never shown again.',
    submitBtn: 'Save connection',
    submitting: 'Saving…',
    successToast: 'Connection saved.',
    errorToast: 'Could not save the connection.',
    providerRequired: 'Enter a provider.',
    apiKeyRequired: 'Enter an API key.',
  },
  card: {
    providerLabel: 'Provider',
    lastTestedLabel: 'Last tested',
    neverTested: 'Never',
    capabilitiesTitle: 'Capabilities',
    noCapabilities: 'No capabilities reported.',
    testBtn: 'Test connection',
    testing: 'Testing…',
    testSuccess: 'Connection test succeeded.',
    testFailure: 'Connection test failed.',
    disableBtn: 'Disable',
    disabling: 'Disabling…',
    disableSuccess: 'Integration disabled.',
    disableError: 'Could not disable the integration.',
  },
  status: {
    label: 'Status',
    not_configured: 'Not configured',
    configured: 'Configured',
    testing: 'Testing',
    active: 'Active',
    degraded: 'Degraded',
    auth_failed: 'Authentication failed',
    disabled: 'Disabled',
  },
  confirmDisable: {
    title: 'Disable integration?',
    description:
      'The assistant will stop reading and writing reservations through this PMS until it is reconfigured.',
    cancelBtn: 'Keep enabled',
    confirmBtn: 'Disable',
  },
  empty: {
    title: 'No PMS connected',
    description: 'Configure your property management system to enable reservation sync.',
  },
  states: {
    loading: 'Loading integration…',
    errorTitle: 'Could not load integration',
    errorDescription: 'Something went wrong loading the integration. Please try again.',
  },
  noAccess: {
    title: 'No access',
    description: 'You do not have permission to manage integrations for this hotel.',
  },
  disabledFlag: {
    title: 'Integrations are off',
    description: 'Integration management is not enabled for this hotel.',
  },
} as const;

export const resources = {
  en: { [namespace]: en },
  bg: { [namespace]: en },
  de: { [namespace]: en },
  fr: { [namespace]: en },
} as const;
